// Client mirror of functions-common/src/repeat/window.ts. Keep in sync.

import { formatZonedDateKey, getWeekBucketKey } from '../../charts/engine/bucket'
import {
  dayKeyToUtcMs,
  extractCadenceKind,
  extractEveryNDaysParams,
  extractPerShiftId,
  resolveWorkspaceTimeZone,
  utcMsToDayKey,
} from './keys'
import {
  type Cadence,
  CadenceNotBuiltError,
  type RecurringWindowInput,
} from './types'

export const MAX_WINDOW_PERIODS = 366

export function periodsInWindow(
  cadence: Cadence,
  window: RecurringWindowInput | null | undefined,
  timeZone?: string,
  until?: Date | string | number | null,
  cap: number = MAX_WINDOW_PERIODS,
): string[] {
  const kind = extractCadenceKind(cadence)
  if (kind === 'ON_DEMAND') {
    throw new CadenceNotBuiltError(kind)
  }

  let perShiftId = ''
  let everyNDaysParams: { intervalDays: number; anchorDayKey: string; anchorMs: number } | null = null
  if (kind === 'PER_SHIFT') {
    perShiftId = extractPerShiftId(cadence)
  } else if (kind === 'EVERY_N_DAYS') {
    everyNDaysParams = extractEveryNDaysParams(cadence)
  }

  if (window?.repeatPaused === true) return []

  const startRaw = window?.startDate
  const endRaw = window?.endDate
  if (!startRaw || !endRaw) return []

  const startIso = startRaw.slice(0, 10)
  const endIso = endRaw.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startIso) || !/^\d{4}-\d{2}-\d{2}$/.test(endIso)) {
    return []
  }
  if (startIso > endIso) return []

  const tz = resolveWorkspaceTimeZone(timeZone)
  let untilIso: string | null = null
  if (until != null) {
    if (until instanceof Date) {
      untilIso = formatZonedDateKey(until, tz)
    } else if (typeof until === 'number') {
      untilIso = formatZonedDateKey(new Date(until), tz)
    } else if (typeof until === 'string') {
      untilIso = until.slice(0, 10)
    }
  }

  if (untilIso && untilIso < startIso) {
    return []
  }

  const effectiveEndIso = untilIso && untilIso < endIso ? untilIso : endIso
  if (effectiveEndIso < startIso) return []

  const [sy, sm, sd] = startIso.split('-').map(Number)
  const [ey, em, ed] = effectiveEndIso.split('-').map(Number)
  const startUtc = new Date(Date.UTC(sy, sm - 1, sd))
  const endUtc = new Date(Date.UTC(ey, em - 1, ed))
  const stopMs = endUtc.getTime()

  const keys: string[] = []
  const seen = new Set<string>()

  switch (kind) {
    case 'DAILY': {
      const cursor = new Date(startUtc)
      while (cursor.getTime() <= stopMs && keys.length < cap) {
        const key = cursor.toISOString().slice(0, 10)
        if (!seen.has(key)) {
          seen.add(key)
          keys.push(key)
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1)
      }
      return keys
    }
    case 'WEEKLY': {
      // WEEKLY: snaps start to Monday of that week
      const dayOfWeek = startUtc.getUTCDay()
      const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek
      const cursor = new Date(startUtc)
      cursor.setUTCDate(cursor.getUTCDate() - (isoDay - 1))

      while (cursor.getTime() <= stopMs && keys.length < cap) {
        const key = getWeekBucketKey(cursor, 'UTC')
        if (!seen.has(key)) {
          seen.add(key)
          keys.push(key)
        }
        cursor.setUTCDate(cursor.getUTCDate() + 7)
      }
      return keys
    }
    case 'MONTHLY': {
      let y = sy
      let m = sm
      while ((y < ey || (y === ey && m <= em)) && keys.length < cap) {
        const key = `${y}-${String(m).padStart(2, '0')}`
        if (!seen.has(key)) {
          seen.add(key)
          keys.push(key)
        }
        m++
        if (m > 12) {
          m = 1
          y++
        }
      }
      return keys
    }
    case 'QUARTERLY': {
      let y = sy
      let q = Math.floor((sm - 1) / 3) + 1
      const eq = Math.floor((em - 1) / 3) + 1
      while ((y < ey || (y === ey && q <= eq)) && keys.length < cap) {
        const key = `${y}-Q${q}`
        if (!seen.has(key)) {
          seen.add(key)
          keys.push(key)
        }
        q++
        if (q > 4) {
          q = 1
          y++
        }
      }
      return keys
    }
    case 'YEARLY': {
      for (let y = sy; y <= ey && keys.length < cap; y++) {
        const key = String(y)
        if (!seen.has(key)) {
          seen.add(key)
          keys.push(key)
        }
      }
      return keys
    }
    case 'EVERY_N_DAYS': {
      const { intervalDays, anchorMs } = everyNDaysParams!
      const startMs = dayKeyToUtcMs(startIso)
      const diffStart = Math.round((startMs - anchorMs) / 86400000)
      const firstOffset = Math.floor(diffStart / intervalDays) * intervalDays
      let cursorMs = anchorMs + firstOffset * 86400000
      const stepMs = intervalDays * 86400000
      while (cursorMs <= stopMs && keys.length < cap) {
        const key = utcMsToDayKey(cursorMs)
        if (!seen.has(key)) {
          seen.add(key)
          keys.push(key)
        }
        cursorMs += stepMs
      }
      return keys
    }
    case 'PER_SHIFT': {
      const cursor = new Date(startUtc)
      while (cursor.getTime() <= stopMs && keys.length < cap) {
        const dayKey = cursor.toISOString().slice(0, 10)
        const key = `${dayKey}~${perShiftId}`
        if (!seen.has(key)) {
          seen.add(key)
          keys.push(key)
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1)
      }
      return keys
    }
    default: {
      const exhaustiveCheck: never = kind
      throw new CadenceNotBuiltError(String(exhaustiveCheck))
    }
  }
}
